import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Truck, AlertCircle, Clock, MapPin, User, X, Wrench, FileText, Filter, LayoutGrid } from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import { fahrzeugApi } from '../api/fahrzeugApi';
import { werkzeugApi } from '../api/werkzeugApi';
import { getAllBenutzer } from '../api/benutzerApi';
import { getAllGeschaeftszeiten } from '../api/kalenderApi';
import { getAllTermine, updateTermin } from '../api/termineApi';

const UNASSIGNED = '__UNASSIGNED__';

// Stabile Farb-Palette für Kategorie-Badges. Strings stehen als Literale im
// Code, damit Tailwinds Content-Scanner sie nicht wegpurgt.
const KATEGORIE_COLORS = [
  'bg-blue-100 text-blue-800',
  'bg-purple-100 text-purple-800',
  'bg-pink-100 text-pink-800',
  'bg-amber-100 text-amber-800',
  'bg-emerald-100 text-emerald-800',
  'bg-orange-100 text-orange-800',
  'bg-cyan-100 text-cyan-800',
  'bg-rose-100 text-rose-800'
];

function hashStr(s) {
  let h = 0;
  for (const ch of (s || '')) h = ((h * 31) + ch.charCodeAt(0)) | 0;
  return Math.abs(h);
}
function kategorieColor(name) {
  return KATEGORIE_COLORS[hashStr(name) % KATEGORIE_COLORS.length];
}

function cardHeight(dauerMinuten) {
  // Min 90px, dann + 0.8px pro Minute. 60min ≈ 138px, 120min ≈ 186px, 240min ≈ 282px
  return Math.max(90, 90 + Math.max(0, (dauerMinuten || 60) - 30) * 0.8);
}

// Timeline-Cell: Zeitfenster aus Geschäftszeiten der Org abgeleitet,
// Fallback auf 08–18 Uhr. Höhe der Cell ist fix in Pixeln.
const TIMELINE_CELL_HEIGHT = 140;
const TIMELINE_DEFAULT_START_MIN = 8 * 60;
const TIMELINE_DEFAULT_END_MIN = 18 * 60;

function parseUhrzeitToMin(uhrzeit) {
  if (!uhrzeit) return 0;
  const [h = '0', m = '0'] = String(uhrzeit).split(':');
  return Math.max(0, Math.min(24 * 60, parseInt(h, 10) * 60 + parseInt(m, 10)));
}

// Berechnet top + height in Pixeln für einen Termin innerhalb einer Tageszelle.
// startMin/endMin = Tages-Envelope der Org. Termine außerhalb werden geclamped.
function timelineBlockGeometry(uhrzeit, dauerMinuten, dayStartMin, dayEndMin) {
  const rangeMin = Math.max(60, dayEndMin - dayStartMin);
  const startMin = parseUhrzeitToMin(uhrzeit);
  const endMin = startMin + (dauerMinuten || 60);
  const outsideBefore = startMin < dayStartMin;
  const outsideAfter = endMin > dayEndMin;
  const clampedStart = Math.max(dayStartMin, Math.min(dayEndMin, startMin));
  const clampedEnd = Math.max(dayStartMin, Math.min(dayEndMin, endMin));
  const top = ((clampedStart - dayStartMin) / rangeMin) * TIMELINE_CELL_HEIGHT;
  const rawH = ((clampedEnd - clampedStart) / rangeMin) * TIMELINE_CELL_HEIGHT;
  const height = Math.max(14, rawH);
  return { top, height, outsideBefore, outsideAfter, startMin, endMin };
}

function formatEndUhrzeit(startUhrzeit, dauerMinuten) {
  const totalMin = parseUhrzeitToMin(startUhrzeit) + (dauerMinuten || 0);
  const h = Math.floor(totalMin / 60) % 24;
  const m = totalMin % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function ymd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isSameLocalDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function formatDateHeader(date) {
  return date.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function Plantafel() {
  const [date, setDate] = useState(() => new Date());
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [werkzeuge, setWerkzeuge] = useState([]);
  const [benutzer, setBenutzer] = useState([]);
  const [termine, setTermine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggingTerminId, setDraggingTerminId] = useState(null);
  const [hoverColumn, setHoverColumn] = useState(null);
  const [hoverWerkzeugColumn, setHoverWerkzeugColumn] = useState(null);

  // Quick-Edit-Panel — panelTerminId steuert Inhalt (bleibt während Close-Animation),
  // panelOpen steuert die Slide-In/Out-Transform.
  const [panelTerminId, setPanelTerminId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelFahrzeugIds, setPanelFahrzeugIds] = useState([]);
  const [panelWerkzeugIds, setPanelWerkzeugIds] = useState([]);
  const [panelBearbeiterId, setPanelBearbeiterId] = useState('');
  const [panelSaving, setPanelSaving] = useState(false);
  const closeTimer = useRef(null);

  // 14-Tage-Window: volle Termin-Liste, daraus werden Strip-Counts UND Gantt-Grid abgeleitet
  const [windowTermine, setWindowTermine] = useState([]);
  const [windowStartDate, setWindowStartDate] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const STRIP_DAYS = 14;

  // View-Mode: 'tag' = klassische Kanban, 'timeline' = Gantt-Lite über 14 Tage
  const [viewMode, setViewMode] = useState('tag');

  // Org-Geschäftszeiten → Envelope für Timeline-Cells
  const [geschaeftszeiten, setGeschaeftszeiten] = useState([]);
  useEffect(() => {
    getAllGeschaeftszeiten()
      .then(data => setGeschaeftszeiten(data || []))
      .catch(err => console.warn('Geschäftszeiten konnten nicht geladen werden, Fallback 08-18:', err));
  }, []);
  // Envelope = früheste Start + späteste End-Zeit über alle aktiven Wochentage,
  // Fallback 08-18 wenn keine konfiguriert. Mindestens 4h Range erzwingen.
  const timelineEnvelope = useMemo(() => {
    const aktiv = geschaeftszeiten.filter(g => g.aktiv && g.startzeit && g.endzeit);
    if (aktiv.length === 0) {
      return { startMin: TIMELINE_DEFAULT_START_MIN, endMin: TIMELINE_DEFAULT_END_MIN };
    }
    let earliest = 24 * 60;
    let latest = 0;
    for (const g of aktiv) {
      earliest = Math.min(earliest, parseUhrzeitToMin(g.startzeit));
      latest = Math.max(latest, parseUhrzeitToMin(g.endzeit));
    }
    if (latest - earliest < 4 * 60) {
      // Sicherheitsnetz bei kaputten Daten
      return { startMin: TIMELINE_DEFAULT_START_MIN, endMin: TIMELINE_DEFAULT_END_MIN };
    }
    return { startMin: earliest, endMin: latest };
  }, [geschaeftszeiten]);

  // Mittag-Marker: 12:00 wenn im Envelope, sonst Mittelpunkt
  const middayMarkerPct = useMemo(() => {
    const midday = 12 * 60;
    if (midday <= timelineEnvelope.startMin || midday >= timelineEnvelope.endMin) {
      return 50;
    }
    return ((midday - timelineEnvelope.startMin) / (timelineEnvelope.endMin - timelineEnvelope.startMin)) * 100;
  }, [timelineEnvelope]);

  const formatRangeLabel = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m === 0 ? `${h}:00` : `${h}:${String(m).padStart(2, '0')}`;
  };
  const envelopeLabel = `${formatRangeLabel(timelineEnvelope.startMin)}–${formatRangeLabel(timelineEnvelope.endMin)}`;

  // Filter-State — leer = kein Filter aktiv
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterKategorieIds, setFilterKategorieIds] = useState([]);
  const [filterBearbeiterIds, setFilterBearbeiterIds] = useState([]); // '' = "Nicht zugewiesen"
  const ARBEITSTAG_MIN = 8 * 60; // Default-Arbeitstag für Auslastungs-Anzeige

  const dayStart = useMemo(() => {
    const d = new Date(date); d.setHours(0, 0, 0, 0); return d;
  }, [date]);
  const dayEnd = useMemo(() => {
    const d = new Date(date); d.setHours(23, 59, 59, 999); return d;
  }, [date]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fzData, wzData, bnData, tmData] = await Promise.all([
        fahrzeugApi.getAll(),
        werkzeugApi.getAll(),
        getAllBenutzer({ aktiv: 'true' }),
        getAllTermine({ startDate: dayStart.toISOString(), endDate: dayEnd.toISOString() })
      ]);
      setFahrzeuge((fzData || []).filter(f => f.status !== 'AUSSORTIERT'));
      setWerkzeuge((wzData || []).filter(w => w.zustand !== 'AUSSORTIERT'));
      setBenutzer(bnData || []);
      // Doppelfilter clientseitig — getAllTermine kann je nach Backend leicht über Tag hinausgehen
      setTermine((tmData || []).filter(t => isSameLocalDay(new Date(t.datum), dayStart)));
    } catch (err) {
      console.error('Plantafel-Daten laden fehlgeschlagen:', err);
      toast.error('Konnte Termine oder Mitarbeiter nicht laden');
    } finally {
      setLoading(false);
    }
  };

  const openPanel = (termin) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setPanelTerminId(termin.id);
    setPanelFahrzeugIds((termin.fahrzeuge || []).map(f => f.fahrzeugId).filter(Boolean));
    setPanelWerkzeugIds((termin.werkzeuge || []).map(w => w.werkzeugId).filter(Boolean));
    setPanelBearbeiterId(termin.bearbeiterId || '');
    // Erst rendern (translate-x-full), dann im nächsten Frame zu translate-x-0 animieren
    requestAnimationFrame(() => setPanelOpen(true));
  };
  const closePanel = () => {
    setPanelOpen(false);
    closeTimer.current = setTimeout(() => {
      setPanelTerminId(null);
      setPanelFahrzeugIds([]);
      setPanelWerkzeugIds([]);
      setPanelBearbeiterId('');
      closeTimer.current = null;
    }, 300);
  };
  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);
  const togglePanelFahrzeug = (id) => {
    setPanelFahrzeugIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const togglePanelWerkzeug = (id) => {
    setPanelWerkzeugIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const savePanel = async () => {
    if (!panelTerminId) return;
    setPanelSaving(true);
    const prevTermine = termine;
    const newBearbeiter = panelBearbeiterId ? benutzer.find(b => b.id === panelBearbeiterId) || null : null;
    // Optimistic
    setTermine(prev => prev.map(t => t.id === panelTerminId ? {
      ...t,
      bearbeiterId: panelBearbeiterId || null,
      bearbeiter: newBearbeiter,
      fahrzeuge: panelFahrzeugIds.map(id => ({ fahrzeugId: id, fahrzeug: fahrzeuge.find(f => f.id === id) || null })),
      werkzeuge: panelWerkzeugIds.map(id => ({ werkzeugId: id, werkzeug: werkzeuge.find(w => w.id === id) || null }))
    } : t));
    try {
      await updateTermin(panelTerminId, {
        fahrzeugIds: panelFahrzeugIds,
        werkzeugIds: panelWerkzeugIds,
        bearbeiterId: panelBearbeiterId || null
      });
      toast.success('Gespeichert');
      closePanel();
      loadWindow();
    } catch (err) {
      console.error('Quick-Edit-Save fehlgeschlagen:', err);
      setTermine(prevTermine);
      if (err?.response?.status === 409) {
        const k = err.response.data?.konflikte || [];
        const msg = k.map(x => `${x.uhrzeit} (${x.dauerMinuten}min)`).join(', ');
        toast.error(`Konflikt: Ressource bereits verplant — ${msg}`);
      } else {
        toast.error('Speichern fehlgeschlagen');
      }
    } finally {
      setPanelSaving(false);
    }
  };

  const panelTermin = useMemo(
    () => termine.find(t => t.id === panelTerminId) || null,
    [termine, panelTerminId]
  );

  useEffect(() => { loadData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dayStart.getTime()]);

  // Lade 14-Tage-Window: ab heute. Volle Termine inkl. Ressourcen-Junctions —
  // daraus werden sowohl Strip-Counts als auch Gantt-Grid abgeleitet.
  const loadWindow = async () => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const stripEnd = new Date(today);
    stripEnd.setDate(stripEnd.getDate() + STRIP_DAYS - 1);
    stripEnd.setHours(23, 59, 59, 999);
    try {
      const data = await getAllTermine({ startDate: today.toISOString(), endDate: stripEnd.toISOString() });
      setWindowStartDate(today);
      setWindowTermine(data || []);
    } catch (err) {
      console.error('14-Tage-Window laden fehlgeschlagen:', err);
    }
  };
  useEffect(() => { loadWindow(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Welche Fahrzeuge/Werkzeuge sind in den nächsten 14 Tagen tatsächlich verplant?
  // Nur diese erscheinen als Zeile in der Timeline-View.
  const { timelineFahrzeuge, timelineWerkzeuge, ungenutzteFahrzeuge, ungenutzteWerkzeuge } = useMemo(() => {
    const usedFz = new Set();
    const usedWz = new Set();
    for (const t of windowTermine) {
      for (const f of (t.fahrzeuge || [])) if (f.fahrzeugId) usedFz.add(f.fahrzeugId);
      for (const w of (t.werkzeuge || [])) if (w.werkzeugId) usedWz.add(w.werkzeugId);
    }
    return {
      timelineFahrzeuge: fahrzeuge.filter(f => usedFz.has(f.id)),
      timelineWerkzeuge: werkzeuge.filter(w => usedWz.has(w.id)),
      ungenutzteFahrzeuge: fahrzeuge.length - fahrzeuge.filter(f => usedFz.has(f.id)).length,
      ungenutzteWerkzeuge: werkzeuge.length - werkzeuge.filter(w => usedWz.has(w.id)).length
    };
  }, [windowTermine, fahrzeuge, werkzeuge]);

  // Strip-Counts aus windowTermine ableiten
  const upcomingSummary = useMemo(() => {
    const map = new Map();
    for (const t of windowTermine) {
      const k = ymd(new Date(t.datum));
      if (!map.has(k)) map.set(k, { count: 0, belegtMin: 0 });
      const entry = map.get(k);
      entry.count++;
      entry.belegtMin += (t.dauerMinuten || 0);
    }
    const days = [];
    for (let i = 0; i < STRIP_DAYS; i++) {
      const d = new Date(windowStartDate);
      d.setDate(d.getDate() + i);
      const stats = map.get(ymd(d)) || { count: 0, belegtMin: 0 };
      days.push({ date: d, ...stats });
    }
    return days;
  }, [windowTermine, windowStartDate]);

  // Verfügbare Kategorien aus den Tages-Terminen ableiten (für Filter-Chips).
  // Stabiles Sort nach Name.
  const verfuegbareKategorien = useMemo(() => {
    const map = new Map();
    for (const t of termine) {
      if (t.kategorie?.id && !map.has(t.kategorie.id)) {
        map.set(t.kategorie.id, t.kategorie);
      }
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [termine]);

  // Termine nach Filter durchreichen — leerer Filter = alle durchlassen.
  // Bearbeiter-Filter: explicit '__UNASSIGNED__' im IDs-Set bedeutet
  // "Termine ohne Bearbeiter" — sonst Match auf bearbeiterId.
  const filteredTermine = useMemo(() => {
    if (filterKategorieIds.length === 0 && filterBearbeiterIds.length === 0) return termine;
    return termine.filter(t => {
      if (filterKategorieIds.length > 0) {
        if (!t.kategorie?.id || !filterKategorieIds.includes(t.kategorie.id)) return false;
      }
      if (filterBearbeiterIds.length > 0) {
        const bId = t.bearbeiterId || UNASSIGNED;
        if (!filterBearbeiterIds.includes(bId)) return false;
      }
      return true;
    });
  }, [termine, filterKategorieIds, filterBearbeiterIds]);

  // Spalten = Unassigned + alle aktiven Fahrzeuge. Termine werden nach
  // erstem zugewiesenem Fahrzeug eingeordnet (Multi-Fahrzeug zeigt Badge "+N").
  // Pro Spalte: belegte Minuten als Auslastungs-Indikator (außer Unassigned).
  const columns = useMemo(() => {
    const cols = [
      { id: UNASSIGNED, label: 'Nicht zugewiesen', sub: 'Termine ohne Fahrzeug', status: null, termine: [], belegtMin: 0, isUnassigned: true },
      ...fahrzeuge.map(f => ({
        id: f.id,
        label: f.kennzeichen,
        sub: f.bezeichnung,
        status: f.status,
        termine: [],
        belegtMin: 0,
        isUnassigned: false
      }))
    ];
    const byId = new Map(cols.map(c => [c.id, c]));
    for (const t of filteredTermine) {
      const fzIds = (t.fahrzeuge || []).map(f => f.fahrzeugId).filter(Boolean);
      const primary = fzIds[0];
      const target = primary && byId.has(primary) ? byId.get(primary) : byId.get(UNASSIGNED);
      target.termine.push(t);
      target.belegtMin += (t.dauerMinuten || 0);
    }
    // Termine pro Spalte nach Uhrzeit sortieren
    for (const c of cols) {
      c.termine.sort((a, b) => (a.uhrzeit || '').localeCompare(b.uhrzeit || ''));
    }
    return cols;
  }, [fahrzeuge, filteredTermine]);

  // Werkzeug-Spalten = Unassigned + alle aktiven Werkzeuge (analog zu Fahrzeug-Spalten).
  // Termin landet in der Spalte seines ersten zugewiesenen Werkzeugs, weitere als "+N"-Badge.
  const werkzeugColumns = useMemo(() => {
    const cols = [
      { id: UNASSIGNED, label: 'Ohne Werkzeug', sub: 'Termine ohne Werkzeug-Zuweisung', zustand: null, termine: [], belegtMin: 0, isUnassigned: true },
      ...werkzeuge.map(w => ({
        id: w.id,
        label: w.bezeichnung,
        sub: w.kategorie || '—',
        zustand: w.zustand,
        termine: [],
        belegtMin: 0,
        isUnassigned: false
      }))
    ];
    const byId = new Map(cols.map(c => [c.id, c]));
    for (const t of filteredTermine) {
      const wzIds = (t.werkzeuge || []).map(w => w.werkzeugId).filter(Boolean);
      const primary = wzIds[0];
      const target = primary && byId.has(primary) ? byId.get(primary) : byId.get(UNASSIGNED);
      target.termine.push(t);
      target.belegtMin += (t.dauerMinuten || 0);
    }
    for (const c of cols) {
      c.termine.sort((a, b) => (a.uhrzeit || '').localeCompare(b.uhrzeit || ''));
    }
    return cols;
  }, [werkzeuge, filteredTermine]);

  const totalFiltered = filteredTermine.length;
  const totalAll = termine.length;
  const filtersAktiv = filterKategorieIds.length > 0 || filterBearbeiterIds.length > 0;
  const resetFilters = () => { setFilterKategorieIds([]); setFilterBearbeiterIds([]); };
  const toggleKategorieFilter = (id) => {
    setFilterKategorieIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleBearbeiterFilter = (id) => {
    setFilterBearbeiterIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Termin-Karte für die Tag-Mode-Kanban-Bänder. Beide Bänder (Fahrzeug + Werkzeug)
  // nutzen das exakt gleiche Card-Layout, daher als Funktion extrahiert.
  const renderTerminCard = (t) => {
    const fzCount = (t.fahrzeuge || []).length;
    const wzCount = (t.werkzeuge || []).length;
    const kundenName = t.kunde?.name || t.titel || 'Privat-Termin';
    const adresse = t.kunde?.adresse || t.ort;
    const projektLabel = t.projekt?.titel || t.projekt?.nummer;
    const anfrageLabel = t.anfrage?.betreff;
    return (
      <div
        key={t.id}
        draggable
        onDragStart={e => handleDragStart(e, t.id)}
        onDragEnd={handleDragEnd}
        onClick={() => openPanel(t)}
        className={`bg-white rounded-lg border border-border p-3 cursor-pointer hover:border-primary hover:shadow-md shadow-sm transition-all flex flex-col ${
          draggingTerminId === t.id ? 'opacity-50' : ''
        }`}
        style={{
          minHeight: `${cardHeight(t.dauerMinuten)}px`,
          borderLeft: t.farbe ? `4px solid ${t.farbe}` : undefined
        }}
      >
        <div className="flex items-center justify-between gap-1.5 mb-1.5">
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <Clock className="w-3 h-3" />
            <span className="font-medium">{t.uhrzeit}</span>
            <span className="text-gray-400">·</span>
            <span>{t.dauerMinuten}min</span>
          </div>
          {t.kategorie?.name && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${kategorieColor(t.kategorie.name)}`}
              title={t.kategorie.name}
            >
              {t.kategorie.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 mb-1">
          <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <span className="truncate">{kundenName}</span>
        </div>
        {adresse && (
          <div className="flex items-start gap-1.5 text-xs text-gray-500 mb-1.5">
            <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
            <span className="line-clamp-2">{adresse}</span>
          </div>
        )}
        {(anfrageLabel || projektLabel) && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1.5">
            <FileText className="w-3 h-3 shrink-0" />
            <span className="truncate">{anfrageLabel || projektLabel}</span>
          </div>
        )}
        {t.bearbeiter && (
          <div className="flex items-center gap-1.5 text-xs mt-auto pt-1.5">
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: t.bearbeiter.farbe || '#9ca3af' }}
            />
            <span className="text-gray-700 truncate">{t.bearbeiter.name}</span>
          </div>
        )}
        {(fzCount > 1 || wzCount > 0) && (
          <div className="flex flex-wrap gap-1 mt-1.5 pt-1.5 border-t border-gray-100">
            {fzCount > 1 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                +{fzCount - 1} weiteres Fhz
              </span>
            )}
            {wzCount > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">
                {wzCount} Werkzeug{wzCount > 1 ? 'e' : ''}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const formatStunden = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h > 0 && m > 0) return `${h}h ${m}min`;
    if (h > 0) return `${h}h`;
    return `${m}min`;
  };

  const goToPrevDay = () => {
    const d = new Date(date); d.setDate(d.getDate() - 1); setDate(d);
  };
  const goToNextDay = () => {
    const d = new Date(date); d.setDate(d.getDate() + 1); setDate(d);
  };
  const goToToday = () => setDate(new Date());

  const handleDragStart = (e, terminId) => {
    setDraggingTerminId(terminId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', terminId);
  };
  const handleDragEnd = () => {
    setDraggingTerminId(null);
    setHoverColumn(null);
    setHoverWerkzeugColumn(null);
  };
  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (hoverColumn !== columnId) setHoverColumn(columnId);
  };
  const handleDragLeave = (columnId) => {
    if (hoverColumn === columnId) setHoverColumn(null);
  };

  // Werkzeug-Band — parallel zur Fahrzeug-Drop-Logik, setzt werkzeugIds statt fahrzeugIds
  const handleWerkzeugDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (hoverWerkzeugColumn !== columnId) setHoverWerkzeugColumn(columnId);
  };
  const handleWerkzeugDragLeave = (columnId) => {
    if (hoverWerkzeugColumn === columnId) setHoverWerkzeugColumn(null);
  };
  const handleWerkzeugDrop = async (e, targetColumnId) => {
    e.preventDefault();
    const terminId = e.dataTransfer.getData('text/plain') || draggingTerminId;
    setHoverWerkzeugColumn(null);
    setDraggingTerminId(null);
    if (!terminId) return;

    const termin = termine.find(t => t.id === terminId);
    if (!termin) return;

    const currentWzIds = (termin.werkzeuge || []).map(w => w.werkzeugId).filter(Boolean);
    const currentPrimary = currentWzIds[0] || UNASSIGNED;
    if (currentPrimary === targetColumnId) return; // No-op

    const nextWzIds = targetColumnId === UNASSIGNED ? [] : [targetColumnId];

    const prevTermine = termine;
    setTermine(prev => prev.map(t => t.id === terminId
      ? { ...t, werkzeuge: nextWzIds.map(id => ({ werkzeugId: id, werkzeug: werkzeuge.find(w => w.id === id) || null })) }
      : t
    ));

    try {
      await updateTermin(terminId, { werkzeugIds: nextWzIds });
      toast.success(targetColumnId === UNASSIGNED ? 'Werkzeug-Zuweisung entfernt' : 'Werkzeug zugewiesen');
      loadWindow();
    } catch (err) {
      console.error('Werkzeug-Drop fehlgeschlagen:', err);
      setTermine(prevTermine);
      if (err?.response?.status === 409) {
        const konflikte = err.response.data?.konflikte || [];
        const msg = konflikte.map(k => `${k.uhrzeit} (${k.dauerMinuten}min)`).join(', ');
        toast.error(`Konflikt: Werkzeug bereits verplant — ${msg}`);
      } else {
        toast.error('Werkzeug-Zuweisung fehlgeschlagen');
      }
    }
  };

  const handleDrop = async (e, targetColumnId) => {
    e.preventDefault();
    const terminId = e.dataTransfer.getData('text/plain') || draggingTerminId;
    setHoverColumn(null);
    setDraggingTerminId(null);
    if (!terminId) return;

    const termin = termine.find(t => t.id === terminId);
    if (!termin) return;

    const currentFzIds = (termin.fahrzeuge || []).map(f => f.fahrzeugId).filter(Boolean);
    const currentPrimary = currentFzIds[0] || UNASSIGNED;
    if (currentPrimary === targetColumnId) return; // No-op

    const nextFzIds = targetColumnId === UNASSIGNED ? [] : [targetColumnId];

    // Optimistic Update
    const prevTermine = termine;
    setTermine(prev => prev.map(t => t.id === terminId
      ? { ...t, fahrzeuge: nextFzIds.map(id => ({ fahrzeugId: id, fahrzeug: fahrzeuge.find(f => f.id === id) || null })) }
      : t
    ));

    try {
      await updateTermin(terminId, { fahrzeugIds: nextFzIds });
      toast.success(targetColumnId === UNASSIGNED ? 'Fahrzeug-Zuweisung entfernt' : 'Termin verschoben');
      loadWindow();
    } catch (err) {
      console.error('Plantafel-Drop fehlgeschlagen:', err);
      setTermine(prevTermine);
      if (err?.response?.status === 409) {
        const konflikte = err.response.data?.konflikte || [];
        const msg = konflikte.map(k => `${k.uhrzeit} (${k.dauerMinuten}min)`).join(', ');
        toast.error(`Konflikt: Fahrzeug bereits verplant — ${msg}`);
      } else {
        toast.error('Verschieben fehlgeschlagen');
      }
    }
  };

  return (
    <Layout>
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-gray-900 flex items-center gap-3">
              <LayoutGrid className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
              Plantafel
            </h1>
            <p className="text-gray-600 text-sm sm:text-base mt-1">
              Termine per Drag&amp;Drop einem Fahrzeug zuweisen
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrevDay}
              className="p-2 border border-border rounded-button hover:bg-gray-50"
              aria-label="Vorheriger Tag"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goToToday}
              className="px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-50"
            >
              Heute
            </button>
            <input
              type="date"
              value={ymd(date)}
              onChange={e => setDate(new Date(e.target.value + 'T12:00:00'))}
              className="px-3 py-2 border border-border rounded-button text-sm"
            />
            <button
              onClick={goToNextDay}
              className="p-2 border border-border rounded-button hover:bg-gray-50"
              aria-label="Nächster Tag"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 14-Tage-Vorschau-Strip */}
        {upcomingSummary.length > 0 && (
          <div className="mb-4 overflow-x-auto">
            <div className="flex gap-1.5 min-w-max">
              {upcomingSummary.map(day => {
                const isSelected = isSameLocalDay(day.date, date);
                const isToday = isSameLocalDay(day.date, new Date());
                const dow = day.date.getDay();
                const isWeekend = dow === 0 || dow === 6;
                const ratio = Math.min(day.belegtMin / ARBEITSTAG_MIN / Math.max(fahrzeuge.length, 1), 1);
                const overbooked = fahrzeuge.length > 0 && day.belegtMin > ARBEITSTAG_MIN * fahrzeuge.length;
                const barColor = overbooked
                  ? 'bg-red-500'
                  : ratio >= 0.7 ? 'bg-amber-500'
                  : ratio >= 0.3 ? 'bg-emerald-500'
                  : day.count > 0 ? 'bg-gray-400'
                  : 'bg-transparent';
                return (
                  <button
                    key={ymd(day.date)}
                    type="button"
                    onClick={() => setDate(day.date)}
                    className={`shrink-0 w-16 px-1.5 py-2 rounded-lg border text-center transition ${
                      isSelected
                        ? 'border-primary bg-primary/10'
                        : isToday
                        ? 'border-primary/40 bg-white hover:bg-gray-50'
                        : isWeekend
                        ? 'border-border bg-gray-50 hover:bg-white'
                        : 'border-border bg-white hover:bg-gray-50'
                    }`}
                    title={day.date.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' })}
                  >
                    <div className={`text-[10px] font-medium uppercase ${isWeekend ? 'text-gray-400' : 'text-gray-500'}`}>
                      {day.date.toLocaleDateString('de-DE', { weekday: 'short' })}
                    </div>
                    <div className={`text-base font-semibold ${
                      isSelected ? 'text-primary' : isToday ? 'text-primary/80' : isWeekend ? 'text-gray-500' : 'text-gray-900'
                    }`}>
                      {day.date.getDate()}.
                    </div>
                    <div className={`text-[10px] ${
                      day.count === 0 ? 'text-gray-300' : 'text-gray-600 font-medium'
                    }`}>
                      {day.count === 0 ? '—' : `${day.count} Termin${day.count !== 1 ? 'e' : ''}`}
                    </div>
                    <div className="h-1 mt-1 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${barColor} transition-all`}
                        style={{ width: day.count > 0 ? `${Math.max(8, ratio * 100)}%` : '0%' }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="text-sm text-gray-500">{viewMode === 'tag' ? formatDateHeader(date) : '14-Tage-Timeline ab heute'}</div>
          <div className="flex items-center gap-2">
            {/* View-Mode-Toggle */}
            <div className="inline-flex rounded-button border border-border bg-white overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode('tag')}
                className={`px-3 py-1.5 text-sm transition ${
                  viewMode === 'tag' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Tag
              </button>
              <button
                type="button"
                onClick={() => setViewMode('timeline')}
                className={`px-3 py-1.5 text-sm transition border-l border-border ${
                  viewMode === 'timeline' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Timeline
              </button>
            </div>
            {filtersAktiv && viewMode === 'tag' && (
              <span className="text-xs text-gray-600">
                {totalFiltered} von {totalAll} {totalAll === 1 ? 'Termin' : 'Terminen'}
              </span>
            )}
            <button
              type="button"
              onClick={() => setFilterOpen(o => !o)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-button text-sm border transition ${
                filtersAktiv
                  ? 'bg-primary/10 text-primary border-primary'
                  : 'bg-white text-text border-border hover:border-primary'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filter
              {filtersAktiv && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary text-white">
                  {filterKategorieIds.length + filterBearbeiterIds.length}
                </span>
              )}
            </button>
            {filtersAktiv && (
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Zurücksetzen
              </button>
            )}
          </div>
        </div>

        {filterOpen && (
          <div className="bg-white border border-border rounded-lg p-4 mb-4 space-y-4">
            {verfuegbareKategorien.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-600 mb-2">Kategorie</div>
                <div className="flex flex-wrap gap-1.5">
                  {verfuegbareKategorien.map(k => {
                    const checked = filterKategorieIds.includes(k.id);
                    return (
                      <button
                        key={k.id}
                        type="button"
                        onClick={() => toggleKategorieFilter(k.id)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition ${
                          checked
                            ? `${kategorieColor(k.name)} border-transparent ring-1 ring-primary`
                            : `${kategorieColor(k.name)} border-transparent opacity-60 hover:opacity-100`
                        }`}
                      >
                        {k.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {benutzer.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-600 mb-2">Mitarbeiter</div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => toggleBearbeiterFilter(UNASSIGNED)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition flex items-center gap-1 ${
                      filterBearbeiterIds.includes(UNASSIGNED)
                        ? 'bg-gray-700 text-white border-gray-700'
                        : 'bg-white text-gray-600 border-border hover:border-primary'
                    }`}
                  >
                    Nicht zugewiesen
                  </button>
                  {benutzer.map(b => {
                    const checked = filterBearbeiterIds.includes(b.id);
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => toggleBearbeiterFilter(b.id)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition flex items-center gap-1.5 ${
                          checked
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-text border-border hover:border-primary'
                        }`}
                      >
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: b.farbe || '#9ca3af' }}
                        />
                        {b.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {verfuegbareKategorien.length === 0 && benutzer.length === 0 && (
              <div className="text-xs text-gray-400">Keine Filter-Optionen verfügbar</div>
            )}
          </div>
        )}

        {loading && (
          <div className="text-center py-12 text-gray-500">Lade Plantafel…</div>
        )}

        {!loading && fahrzeuge.length === 0 && (
          <div className="bg-white border border-border rounded-lg p-8 text-center">
            <Truck className="w-12 h-12 mx-auto text-gray-400 mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Noch keine Fahrzeuge angelegt</h3>
            <p className="text-gray-500 text-sm mb-4">
              Lege im Katalog Fahrzeuge an, damit du Termine zuweisen kannst.
            </p>
            <Link
              to="/katalog?tab=fahrzeuge"
              className="inline-block bg-primary text-white px-4 py-2 rounded-button text-sm font-medium hover:bg-primary-dark"
            >
              Zum Fahrzeug-Katalog
            </Link>
          </div>
        )}

        {/* Empty-State: Timeline aktiv, aber keine Ressource ist die nächsten 14 Tage verplant */}
        {!loading && viewMode === 'timeline' && timelineFahrzeuge.length === 0 && timelineWerkzeuge.length === 0 && (
          <div className="bg-white border border-border rounded-lg p-8 text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-gray-400 mb-2" />
            <h3 className="text-base font-medium text-gray-900 mb-1">In den nächsten 14 Tagen ist keine Ressource verplant</h3>
            <p className="text-gray-500 text-sm">
              Sobald du Fahrzeuge oder Werkzeuge an Termine zuweist, erscheinen sie hier als Zeile.
            </p>
            {(fahrzeuge.length > 0 || werkzeuge.length > 0) && (
              <p className="text-xs text-gray-400 mt-2">
                {fahrzeuge.length} Fahrzeug{fahrzeuge.length !== 1 ? 'e' : ''} und{' '}
                {werkzeuge.length} Werkzeug{werkzeuge.length !== 1 ? 'e' : ''} im Katalog
              </p>
            )}
          </div>
        )}

        {/* Gantt-Lite Timeline-View — 14 Tage, nur Ressourcen mit aktiver Einplanung */}
        {!loading && (timelineFahrzeuge.length > 0 || timelineWerkzeuge.length > 0) && viewMode === 'timeline' && (
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <div className="min-w-max">
                {/* Header-Row: Tage */}
                <div className="flex border-b border-border bg-gray-50 sticky top-0 z-10">
                  <div className="w-44 shrink-0 px-3 py-2 text-xs font-semibold text-gray-700 border-r border-border">
                    Ressource
                  </div>
                  {upcomingSummary.map(day => {
                    const isToday = isSameLocalDay(day.date, new Date());
                    const dow = day.date.getDay();
                    const isWeekend = dow === 0 || dow === 6;
                    return (
                      <button
                        type="button"
                        key={ymd(day.date)}
                        onClick={() => { setDate(day.date); setViewMode('tag'); }}
                        title="Zum Tag wechseln"
                        className={`w-24 shrink-0 px-2 py-1.5 text-center border-r border-border last:border-r-0 transition ${
                          isToday ? 'bg-primary/10' : isWeekend ? 'bg-gray-100' : 'bg-gray-50'
                        } hover:bg-primary/5`}
                      >
                        <div className={`text-[10px] uppercase ${isWeekend ? 'text-gray-400' : 'text-gray-500'}`}>
                          {day.date.toLocaleDateString('de-DE', { weekday: 'short' })}
                        </div>
                        <div className={`text-sm font-semibold ${isToday ? 'text-primary' : isWeekend ? 'text-gray-500' : 'text-gray-900'}`}>
                          {day.date.getDate()}.{day.date.getMonth() + 1}.
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Fahrzeug-Section — nur Fahrzeuge mit mind. einem Termin in den 14 Tagen */}
                {timelineFahrzeuge.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50 border-b border-border flex items-center justify-between">
                      <span>Fahrzeuge ({timelineFahrzeuge.length})</span>
                      {ungenutzteFahrzeuge > 0 && (
                        <span className="text-gray-400 normal-case">
                          {ungenutzteFahrzeuge} ungenutzt
                        </span>
                      )}
                    </div>
                    {timelineFahrzeuge.map(f => {
                      const wartung = f.naechsteWartung ? new Date(f.naechsteWartung) : null;
                      return (
                        <div key={`fz-${f.id}`} className="flex border-b border-border hover:bg-gray-50/50">
                          <div className="w-44 shrink-0 px-3 py-2 border-r border-border flex items-center gap-2">
                            <Truck className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <div className="min-w-0">
                              <div className="text-xs font-semibold text-gray-900 truncate">{f.kennzeichen}</div>
                              <div className="text-[10px] text-gray-500 truncate">{f.bezeichnung}</div>
                            </div>
                            {f.status === 'WARTUNG' && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 shrink-0">W</span>
                            )}
                            {f.status === 'AUSFALL' && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 text-red-800 shrink-0">A</span>
                            )}
                          </div>
                          {upcomingSummary.map(day => {
                            const dayTermine = windowTermine.filter(t =>
                              isSameLocalDay(new Date(t.datum), day.date)
                              && (t.fahrzeuge || []).some(x => x.fahrzeugId === f.id)
                            ).sort((a, b) => (a.uhrzeit || '').localeCompare(b.uhrzeit || ''));
                            const dow = day.date.getDay();
                            const isWeekend = dow === 0 || dow === 6;
                            const isWartungstag = wartung && isSameLocalDay(wartung, day.date);
                            return (
                              <div
                                key={ymd(day.date)}
                                style={{ height: TIMELINE_CELL_HEIGHT }}
                                className={`w-24 shrink-0 border-r border-border last:border-r-0 relative ${
                                  isWeekend ? 'bg-gray-50/50' : 'bg-white'
                                }`}
                              >
                                {/* Stunden-Marker: 12 Uhr Mittag als feine Linie */}
                                <div className="absolute inset-x-0 border-t border-dashed border-gray-200 pointer-events-none" style={{ top: `${middayMarkerPct}%` }} />
                                {/* Wartung-Tag: ganzer Streifen über die Cell */}
                                {isWartungstag && (
                                  <div className="absolute inset-x-1 top-1 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-center font-medium z-10">
                                    Wartung
                                  </div>
                                )}
                                {dayTermine.map(t => {
                                  const { top, height, outsideBefore, outsideAfter } = timelineBlockGeometry(t.uhrzeit, t.dauerMinuten, timelineEnvelope.startMin, timelineEnvelope.endMin);
                                  const colorCls = t.kategorie?.name ? kategorieColor(t.kategorie.name) : 'bg-gray-200 text-gray-700';
                                  const kundenName = t.kunde?.name || t.titel || 'Privat';
                                  const endU = formatEndUhrzeit(t.uhrzeit, t.dauerMinuten);
                                  const showFull = height >= 32;
                                  return (
                                    <button
                                      type="button"
                                      key={t.id}
                                      onClick={() => { setDate(new Date(t.datum)); openPanel(t); }}
                                      title={`${t.uhrzeit}–${endU} (${t.dauerMinuten}min) · ${kundenName}${t.kategorie?.name ? ' · ' + t.kategorie.name : ''}`}
                                      style={{ position: 'absolute', top: `${top}px`, height: `${height}px`, left: '2px', right: '2px' }}
                                      className={`rounded text-[9px] px-1 text-left font-medium leading-tight transition hover:ring-2 hover:ring-primary hover:z-20 overflow-hidden ${colorCls} ${
                                        outsideBefore ? 'border-t-2 border-t-primary' : ''
                                      } ${outsideAfter ? 'border-b-2 border-b-primary' : ''}`}
                                    >
                                      <div className="truncate">{t.uhrzeit}–{endU}</div>
                                      {showFull && (
                                        <div className="truncate opacity-80">{kundenName}</div>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </>
                )}

                {/* Werkzeug-Section — nur Werkzeuge mit mind. einem Termin in den 14 Tagen */}
                {timelineWerkzeuge.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50 border-b border-border border-t flex items-center justify-between">
                      <span>Werkzeuge ({timelineWerkzeuge.length})</span>
                      {ungenutzteWerkzeuge > 0 && (
                        <span className="text-gray-400 normal-case">
                          {ungenutzteWerkzeuge} ungenutzt
                        </span>
                      )}
                    </div>
                    {timelineWerkzeuge.map(w => (
                      <div key={`wz-${w.id}`} className="flex border-b border-border last:border-b-0 hover:bg-gray-50/50">
                        <div className="w-44 shrink-0 px-3 py-2 border-r border-border flex items-center gap-2">
                          <Wrench className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-gray-900 truncate">{w.bezeichnung}</div>
                            {w.kategorie && (
                              <div className="text-[10px] text-gray-500 truncate">{w.kategorie}</div>
                            )}
                          </div>
                          {w.zustand === 'DEFEKT' && (
                            <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 text-red-800 shrink-0">def.</span>
                          )}
                        </div>
                        {upcomingSummary.map(day => {
                          const dayTermine = windowTermine.filter(t =>
                            isSameLocalDay(new Date(t.datum), day.date)
                            && (t.werkzeuge || []).some(x => x.werkzeugId === w.id)
                          ).sort((a, b) => (a.uhrzeit || '').localeCompare(b.uhrzeit || ''));
                          const dow = day.date.getDay();
                          const isWeekend = dow === 0 || dow === 6;
                          return (
                            <div
                              key={ymd(day.date)}
                              style={{ height: TIMELINE_CELL_HEIGHT }}
                              className={`w-24 shrink-0 border-r border-border last:border-r-0 relative ${
                                isWeekend ? 'bg-gray-50/50' : 'bg-white'
                              }`}
                            >
                              <div className="absolute inset-x-0 border-t border-dashed border-gray-200 pointer-events-none" style={{ top: `${middayMarkerPct}%` }} />
                              {dayTermine.map(t => {
                                const { top, height, outsideBefore, outsideAfter } = timelineBlockGeometry(t.uhrzeit, t.dauerMinuten, timelineEnvelope.startMin, timelineEnvelope.endMin);
                                const colorCls = t.kategorie?.name ? kategorieColor(t.kategorie.name) : 'bg-gray-200 text-gray-700';
                                const kundenName = t.kunde?.name || t.titel || 'Privat';
                                const endU = formatEndUhrzeit(t.uhrzeit, t.dauerMinuten);
                                const showFull = height >= 32;
                                return (
                                  <button
                                    type="button"
                                    key={t.id}
                                    onClick={() => { setDate(new Date(t.datum)); openPanel(t); }}
                                    title={`${t.uhrzeit}–${endU} (${t.dauerMinuten}min) · ${kundenName}`}
                                    style={{ position: 'absolute', top: `${top}px`, height: `${height}px`, left: '2px', right: '2px' }}
                                    className={`rounded text-[9px] px-1 text-left font-medium leading-tight transition hover:ring-2 hover:ring-primary hover:z-20 overflow-hidden ${colorCls} ${
                                      outsideBefore ? 'border-t-2 border-t-primary' : ''
                                    } ${outsideAfter ? 'border-b-2 border-b-primary' : ''}`}
                                  >
                                    <div className="truncate">{t.uhrzeit}–{endU}</div>
                                    {showFull && (
                                      <div className="truncate opacity-80">{kundenName}</div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>

            {/* Legende */}
            <div className="px-3 py-2 border-t border-border bg-gray-50 flex items-center gap-3 flex-wrap text-[10px] text-gray-600">
              <span>
                Zeitfenster {envelopeLabel} Uhr (Geschäftszeiten){' '}
                {middayMarkerPct >= 5 && middayMarkerPct <= 95 ? '· gestrichelte Linie = 12 Uhr ' : ''}
                · Position ∝ Uhrzeit, Höhe ∝ Dauer · Klick öffnet Termin
              </span>
              <span className="ml-auto">{windowTermine.length} Termine in 14 Tagen</span>
            </div>
          </div>
        )}

        {!loading && fahrzeuge.length > 0 && viewMode === 'tag' && (
          <>
            <div className="flex items-center gap-2 mb-3">
              <Truck className="w-4 h-4 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Fahrzeuge</h2>
              <span className="text-xs text-gray-400">Termin auf Fahrzeug-Spalte ziehen, um es zuzuweisen</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-4">
            {columns.map(col => (
              <div
                key={col.id}
                onDragOver={e => handleDragOver(e, col.id)}
                onDragLeave={() => handleDragLeave(col.id)}
                onDrop={e => handleDrop(e, col.id)}
                className={`shrink-0 w-72 bg-gray-50 rounded-lg border-2 transition ${
                  hoverColumn === col.id ? 'border-primary bg-primary/5' : 'border-transparent'
                }`}
              >
                <div className="px-3 py-2.5 border-b border-border bg-white rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-gray-900 truncate">{col.label}</div>
                      <div className="text-xs text-gray-500 truncate">{col.sub}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      {col.status === 'WARTUNG' && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800" title="In Wartung">
                          Wartung
                        </span>
                      )}
                      {col.status === 'AUSFALL' && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800" title="Ausgefallen">
                          Ausfall
                        </span>
                      )}
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {col.termine.length}
                      </span>
                    </div>
                  </div>

                  {/* Auslastungs-Indikator — nur für Fahrzeug-Spalten, nicht für Unassigned */}
                  {!col.isUnassigned && (() => {
                    const ratio = Math.min(col.belegtMin / ARBEITSTAG_MIN, 1);
                    const overbooked = col.belegtMin > ARBEITSTAG_MIN;
                    const barColor = overbooked
                      ? 'bg-red-500'
                      : ratio >= 0.85 ? 'bg-amber-500'
                      : ratio >= 0.5 ? 'bg-emerald-500'
                      : 'bg-gray-400';
                    return (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-[10px] text-gray-500 mb-0.5">
                          <span>{col.belegtMin > 0 ? formatStunden(col.belegtMin) : '—'} / 8h</span>
                          {overbooked && (
                            <span className="text-red-600 font-medium">Überbucht</span>
                          )}
                        </div>
                        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${barColor} transition-all`}
                            style={{ width: `${Math.min(ratio * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="p-2 space-y-2 min-h-[120px]">
                  {col.termine.length === 0 && (
                    <div className="text-xs text-gray-400 text-center py-6">Hierhin ziehen</div>
                  )}
                  {col.termine.map(t => renderTerminCard(t))}
                </div>
              </div>
            ))}
            </div>
          </>
        )}

        {/* Werkzeug-Band — zweite Kanban-Reihe, Spalten = Werkzeuge */}
        {!loading && werkzeuge.length > 0 && viewMode === 'tag' && (
          <>
            <div className="flex items-center gap-2 mt-6 mb-3">
              <Wrench className="w-4 h-4 text-gray-400" />
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Werkzeuge</h2>
              <span className="text-xs text-gray-400">Termin auf Werkzeug-Spalte ziehen, um es zuzuweisen</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-4">
              {werkzeugColumns.map(col => (
                <div
                  key={col.id}
                  onDragOver={e => handleWerkzeugDragOver(e, col.id)}
                  onDragLeave={() => handleWerkzeugDragLeave(col.id)}
                  onDrop={e => handleWerkzeugDrop(e, col.id)}
                  className={`shrink-0 w-72 bg-gray-50 rounded-lg border-2 transition ${
                    hoverWerkzeugColumn === col.id ? 'border-primary bg-primary/5' : 'border-transparent'
                  }`}
                >
                  <div className="px-3 py-2.5 border-b border-border bg-white rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm text-gray-900 truncate">{col.label}</div>
                        <div className="text-xs text-gray-500 truncate">{col.sub}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 ml-2">
                        {col.zustand === 'DEFEKT' && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800" title="Defekt">
                            Defekt
                          </span>
                        )}
                        {col.zustand === 'GELEAST' && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-800" title="Geleast">
                            Leasing
                          </span>
                        )}
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {col.termine.length}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="p-2 space-y-2 min-h-[120px]">
                    {col.termine.length === 0 && (
                      <div className="text-xs text-gray-400 text-center py-6">Hierhin ziehen</div>
                    )}
                    {col.termine.map(t => renderTerminCard(t))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && fahrzeuge.length > 0 && filteredTermine.length === 0 && (
          <div className="text-center text-sm text-gray-500 mt-6 flex flex-col items-center gap-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {filtersAktiv
                ? `Keine Termine entsprechen dem Filter (${totalAll} insgesamt am Tag)`
                : 'Keine Termine an diesem Tag'}
            </div>
            {filtersAktiv && (
              <button onClick={resetFilters} className="text-xs text-primary hover:underline">
                Filter zurücksetzen
              </button>
            )}
          </div>
        )}
      </div>

      {/* Quick-Edit-Panel — Termin-Ressourcen pflegen */}
      {/* Backdrop — Fade-in, immer gemountet wenn Panel-Inhalt vorhanden */}
      <div
        onClick={closePanel}
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${
          panelOpen && panelTermin ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Quick-Edit-Panel — Slide-in von rechts. Inhalt bleibt während Close-Animation */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-xl z-50 flex flex-col transform transition-transform duration-300 ease-out ${
          panelOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!panelOpen}
      >
        {panelTermin && (<>
          <div className="px-5 py-4 border-b border-border flex items-start justify-between">
            <div className="min-w-0">
              <div className="text-xs text-gray-500">{formatDateHeader(new Date(panelTermin.datum))}</div>
              <div className="font-semibold text-gray-900 truncate">
                {panelTermin.kunde?.name || panelTermin.titel || 'Privat-Termin'}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-600 mt-0.5">
                <Clock className="w-3 h-3" />
                <span>{panelTermin.uhrzeit} · {panelTermin.dauerMinuten}min</span>
                {panelTermin.kategorie?.name && (
                  <>
                    <span className="text-gray-400">·</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${kategorieColor(panelTermin.kategorie.name)}`}>
                      {panelTermin.kategorie.name}
                    </span>
                  </>
                )}
              </div>
              {(panelTermin.kunde?.adresse || panelTermin.ort) && (
                <div className="flex items-start gap-1.5 text-xs text-gray-500 mt-1">
                  <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>{panelTermin.kunde?.adresse || panelTermin.ort}</span>
                </div>
              )}
              {(panelTermin.anfrage?.betreff || panelTermin.projekt?.titel) && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                  <FileText className="w-3 h-3 shrink-0" />
                  <span className="truncate">{panelTermin.anfrage?.betreff || panelTermin.projekt?.titel}</span>
                </div>
              )}
            </div>
            <button
              onClick={closePanel}
              className="p-1 hover:bg-gray-100 rounded shrink-0 ml-2"
              aria-label="Schließen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            {/* Mitarbeiter (Single-Select) */}
            <div>
              <div className="flex items-center gap-2 text-sm font-medium mb-2">
                <User className="w-4 h-4 text-gray-500" />
                Mitarbeiter
              </div>
              <select
                value={panelBearbeiterId}
                onChange={e => setPanelBearbeiterId(e.target.value)}
                disabled={panelSaving}
                className="w-full px-3 py-2 border border-border rounded-button text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">— Nicht zugewiesen —</option>
                {benutzer.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm font-medium mb-2">
                <Truck className="w-4 h-4 text-gray-500" />
                Fahrzeuge
                {panelFahrzeugIds.length > 0 && (
                  <span className="text-xs font-normal text-gray-500">
                    ({panelFahrzeugIds.length} ausgewählt)
                  </span>
                )}
              </div>
              {fahrzeuge.length === 0 ? (
                <div className="text-xs text-gray-400">
                  Keine Fahrzeuge im Katalog —{' '}
                  <Link to="/katalog?tab=fahrzeuge" className="text-primary hover:underline">
                    jetzt anlegen
                  </Link>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {fahrzeuge.map(f => {
                    const checked = panelFahrzeugIds.includes(f.id);
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => togglePanelFahrzeug(f.id)}
                        disabled={panelSaving}
                        title={f.status !== 'VERFUEGBAR' ? f.status : ''}
                        className={`px-3 py-1.5 rounded-button text-xs border transition ${
                          checked
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-text border-border hover:border-primary'
                        }`}
                      >
                        {f.kennzeichen} · {f.bezeichnung}
                        {f.status !== 'VERFUEGBAR' && <span className="ml-1">⚠</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm font-medium mb-2">
                <Wrench className="w-4 h-4 text-gray-500" />
                Werkzeuge
                {panelWerkzeugIds.length > 0 && (
                  <span className="text-xs font-normal text-gray-500">
                    ({panelWerkzeugIds.length} ausgewählt)
                  </span>
                )}
              </div>
              {werkzeuge.length === 0 ? (
                <div className="text-xs text-gray-400">
                  Keine Werkzeuge im Katalog —{' '}
                  <Link to="/katalog?tab=werkzeuge" className="text-primary hover:underline">
                    jetzt anlegen
                  </Link>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {werkzeuge.map(w => {
                    const checked = panelWerkzeugIds.includes(w.id);
                    const disabled = w.zustand === 'DEFEKT';
                    return (
                      <button
                        key={w.id}
                        type="button"
                        onClick={() => !disabled && togglePanelWerkzeug(w.id)}
                        disabled={panelSaving || disabled}
                        title={w.zustand === 'DEFEKT' ? 'Defekt' : w.kategorie || ''}
                        className={`px-3 py-1.5 rounded-button text-xs border transition ${
                          checked
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-text border-border hover:border-primary'
                        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {w.bezeichnung}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-4 border-t border-border flex gap-2">
            <button
              onClick={closePanel}
              disabled={panelSaving}
              className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-button text-sm font-medium disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              onClick={savePanel}
              disabled={panelSaving}
              className="flex-1 px-4 py-2.5 bg-primary text-white hover:bg-primary-dark rounded-button text-sm font-medium disabled:opacity-50"
            >
              {panelSaving ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </>)}
      </div>
    </Layout>
  );
}
